import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { RIDES, RIDE_TUNING, WORLD, cleanName } from '@thrasher/shared';
import type { Ride, ServerStatus } from '@thrasher/shared';
import { Game } from '../game/Game.js';
import { HudStore } from '../game/hudStore.js';
import type { Quality } from '../game/Renderer.js';
import { getIdentity, lobbyUrl } from '../hub/identity.js';
import { CONTROLS, Hud } from './Hud.js';
import { RideIcon } from './RideIcon.js';

const RIDE_KEY = 'thrasher.ride';
const NAME_KEY = 'thrasher.name';
const QUALITY_KEY = 'thrasher.quality';

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable (private mode); preferences are optional.
  }
}

function initialRide(): Ride {
  const saved = readStorage(RIDE_KEY);
  return (RIDES as readonly string[]).includes(saved ?? '') ? (saved as Ride) : 'skateboard';
}

function initialQuality(): Quality {
  const saved = readStorage(QUALITY_KEY);
  if (saved === 'high' || saved === 'medium' || saved === 'low') return saved;
  const mobile = /Mobi|Android/i.test(navigator.userAgent);
  return mobile ? 'low' : 'high';
}

const STAT_MAX = { speed: 15, air: 10, spin: 8.5, grind: 1.2 };

function RideCard({
  ride,
  selected,
  onSelect,
}: {
  ride: Ride;
  selected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  const tuning = RIDE_TUNING[ride];
  const stats: [string, number][] = [
    ['Speed', tuning.pushSpeed / STAT_MAX.speed],
    ['Air', tuning.chargedOllie / STAT_MAX.air],
    ['Spin', tuning.spinRate / STAT_MAX.spin],
    ['Grind', (1.3 - tuning.balanceDrift) / STAT_MAX.grind + 0.35],
  ];
  return (
    <button
      type="button"
      className={`ride-card ${selected ? 'selected' : ''}`}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <RideIcon ride={ride} size={40} />
      <strong>{tuning.label}</strong>
      <span className="blurb">{tuning.blurb}</span>
      <div className="stats">
        {stats.map(([label, value]) => (
          <div key={label} className="stat">
            <span>{label}</span>
            <div className="bar">
              <div style={{ width: `${Math.max(0.15, Math.min(1, value)) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </button>
  );
}

export function App(): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const store = useMemo(() => new HudStore(), []);
  const hud = useSyncExternalStore(store.subscribe, store.get);
  const [game, setGame] = useState<Game | null>(null);
  const [ride, setRide] = useState<Ride>(initialRide);
  const identity = useMemo(() => getIdentity(), []);
  const [guestName, setGuestName] = useState(() => readStorage(NAME_KEY) ?? '');
  const [status, setStatus] = useState<ServerStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const offline = params.has('offline');
  const hideUi = params.has('hideui');
  const linkedName = identity.token ? identity.name : null;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    let created: Game | null = null;
    // Let the entry screen paint before the park is generated.
    const timer = setTimeout(() => {
      try {
        const requested = params.get('quality');
        const quality =
          requested === 'high' || requested === 'medium' || requested === 'low'
            ? requested
            : initialQuality();
        const maxFrames = Number(params.get('frames')) || null;
        created = new Game({
          container,
          store,
          quality,
          offline,
          shot: params.get('shot'),
          maxFrames,
        });
        created.setToken(identity.token);
        setGame(created);
      } catch (error) {
        console.error(error);
        setLoadError(
          'Thrasher needs WebGL. Try a current version of Chrome, Edge, Firefox or Safari.',
        );
      }
    }, 30);
    return () => {
      clearTimeout(timer);
      created?.dispose();
    };
  }, [store, offline, params, identity.token]);

  useEffect(() => {
    if (offline || hud.phase === 'playing') return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await fetch('/api/status', { cache: 'no-store' });
        if (response.ok && !cancelled) setStatus((await response.json()) as ServerStatus);
      } catch {
        // Status is decorative on the entry screen.
      }
    };
    void poll();
    const interval = setInterval(() => void poll(), 5_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [offline, hud.phase]);

  const autojoined = useRef(false);
  useEffect(() => {
    if (game && params.has('autojoin') && hud.phase === 'preview' && !autojoined.current) {
      autojoined.current = true;
      void game.join(linkedName ?? (guestName || 'Tester'), ride, identity.token);
    }
  }, [game, params, hud.phase, linkedName, guestName, ride, identity.token]);

  const dropIn = () => {
    if (!game) return;
    writeStorage(RIDE_KEY, ride);
    const name = linkedName ?? (cleanName(guestName) || '');
    if (!linkedName && name) writeStorage(NAME_KEY, name);
    void game.join(name, ride, identity.token);
  };

  useEffect(() => {
    writeStorage(QUALITY_KEY, hud.quality);
  }, [hud.quality]);

  const full = status !== null && status.riders >= status.maxRiders;
  const showEntry = hud.phase === 'preview' || hud.phase === 'joining' || hud.phase === 'error';

  return (
    <div className="app">
      <div ref={containerRef} className="stage" />
      {game && hud.phase === 'playing' && !hideUi && <Hud store={store} game={game} />}
      {showEntry && !hideUi && (
        <div className="entry">
          <a className="hub-link" href={lobbyUrl()}>
            ← Games Hub
          </a>
          <div className="entry-panel">
            <h1 className="logo">
              <span>THRASHER</span>
            </h1>
            <p className="tagline">One giant park. Skate, BMX or blade it. Throw hands.</p>

            <div className="identity">
              {linkedName ? (
                <>
                  <span className="label">Riding as</span>
                  <strong>{linkedName}</strong>
                  <span className="hub-badge">Games Hub profile</span>
                </>
              ) : (
                <>
                  <label className="label" htmlFor="guest-name">
                    Rider name
                  </label>
                  <input
                    id="guest-name"
                    value={guestName}
                    maxLength={WORLD.nameMaxLength}
                    placeholder="Guest rider"
                    onChange={(event) => setGuestName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') dropIn();
                    }}
                  />
                  <a className="hub-hint" href={lobbyUrl()}>
                    Enter from the Games Hub to use your profile and earn coins
                  </a>
                </>
              )}
            </div>

            <div className="ride-picker" role="group" aria-label="Choose your ride">
              {RIDES.map((option) => (
                <RideCard
                  key={option}
                  ride={option}
                  selected={ride === option}
                  onSelect={() => setRide(option)}
                />
              ))}
            </div>

            <button
              type="button"
              className="drop-in"
              disabled={!game || hud.phase === 'joining' || full}
              onClick={dropIn}
            >
              {!game
                ? 'Building the park…'
                : hud.phase === 'joining'
                  ? 'Dropping in…'
                  : full
                    ? 'Park is full'
                    : 'Drop in'}
            </button>
            <div className="live">
              <span className="dot" />
              {offline
                ? 'Offline sandbox'
                : status
                  ? `${status.riders} of ${status.maxRiders} riders in the park right now${(status.bots ?? 0) > 0 ? ` · ${status.bots} bots` : ''}`
                  : 'One shared session: everyone rides the same park'}
            </div>
            <div className="live-note">
              When the park is quiet, bots drop in so there is always someone to punch.
            </div>
            {(hud.error || loadError) && <div className="error">{hud.error ?? loadError}</div>}
          </div>

          <div className="entry-controls">
            <h2>Controls</h2>
            <ul className="controls">
              {CONTROLS.slice(0, 9).map(([key, label]) => (
                <li key={key}>
                  <kbd>{key}</kbd>
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
